import type { User } from '../../contexts/UserContext';
import { canManageSelectedCompany } from './companyAdminAccess';
import { APP_NAV_FEATURE_CODES, type AppNavFeatureCode } from './appNavFeatureCodes';

export interface AppNavAccess {
  /** Krysalis Central platform operator (superuser / platform admin). */
  isPlatformAdmin: boolean;
  /** Company manager for the header-selected company, or a platform administrator. */
  canManageCompany: boolean;
  /** Can open subscription assignment UIs (platform admin today). */
  canManageSubscriptions: boolean;
  /** Subscription-gated nav areas resolved for current user + company. */
  features: Record<AppNavFeatureCode, boolean>;
}

const DEFAULT_FEATURES: Record<AppNavFeatureCode, boolean> = {
  [APP_NAV_FEATURE_CODES.primeArtifacts]: false,
  [APP_NAV_FEATURE_CODES.artifactConfiguration]: false,
};

/** Platform / central admin — gates Platform administration nav and admin tools. */
export function isPlatformAdministrator(user: User | null | undefined): boolean {
  if (!user) return false;
  const role = (user.role ?? '').toLowerCase().replace(/-/g, '_');
  if (
    role === 'admin' ||
    role === 'superuser' ||
    role === 'administrator' ||
    role === 'platform_admin'
  ) {
    return true;
  }
  const permissions = user.permissions ?? [];
  if (permissions.includes('*')) return true;
  // Company-scoped grants such as manage:users do not open platform routes.
  return permissions.some(
    (p) => p === 'manage:platform' || p === 'system:admin',
  );
}

/** Resources library authoring (platform admin until subscription feature-code lands). */
export function canAccessResourcesAdmin(user: User | null | undefined): boolean {
  return isPlatformAdministrator(user);
}

/** Subscription assignment / catalog admin chrome (align hamburger + SideNav). */
export function canManageSubscriptions(user: User | null | undefined): boolean {
  return isPlatformAdministrator(user);
}

export function buildDefaultNavAccess(
  user: User | null | undefined,
  options?: { membershipRole?: string | null },
): AppNavAccess {
  const isPlatformAdmin = isPlatformAdministrator(user);
  return {
    isPlatformAdmin,
    canManageCompany: canManageSelectedCompany(user, options?.membershipRole),
    canManageSubscriptions: canManageSubscriptions(user),
    features: {
      ...DEFAULT_FEATURES,
      ...(isPlatformAdmin
        ? {
            [APP_NAV_FEATURE_CODES.primeArtifacts]: true,
            [APP_NAV_FEATURE_CODES.artifactConfiguration]: true,
          }
        : {}),
    },
  };
}

export function mergeNavFeatureAccess(
  base: AppNavAccess,
  featureResults: Partial<Record<AppNavFeatureCode, boolean>>,
): AppNavAccess {
  return {
    ...base,
    features: {
      ...base.features,
      ...featureResults,
    },
  };
}

/** Prime artifact modeling (create tree, apply-from-prime, prime nav). */
export function canUsePrimeArtifactFeatures(access: AppNavAccess): boolean {
  return (
    access.isPlatformAdmin || access.features[APP_NAV_FEATURE_CODES.primeArtifacts]
  );
}
